import { Action } from "@packages/shared-types";
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import {
	ArrowLeftRight,
	BarChart3,
	HandCoins,
	Landmark,
	LayoutDashboard,
	Receipt,
	Repeat,
	Truck,
	Wallet,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { HeaderSlotTarget } from "@/components/layout/HeaderSlot";
import type { TabItem } from "@/components/TabNavigation";
import { TabNavigation } from "@/components/TabNavigation";
import { useHasPermission } from "@/hooks/useAccessControl";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { FINANCIAL_HEADER_ACTIONS_SLOT } from "./-components/FinancialHeaderActions";
import { keepScope } from "./-components/farmScopeSearch";

// Section layout guard: financial is online-only by design (authoritative
// money state). Covers every child route via ancestor stance resolution.
export const Route = createFileRoute("/_auth/app/_app/org/$orgId/financial")({
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	component: FinancialSectionLayout,
});

// Sub-path segments checked against the pathname to resolve the active tab.
// Anything that isn't a known sub-section falls back to the overview.
const SUB_SECTIONS = [
	"transactions",
	"vendors",
	"reports",
	"loans",
	"recurring",
	"receivables",
	"payables",
	"payroll",
] as const;

function FinancialSectionLayout() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const location = useLocation();
	const can = useHasPermission(orgId);

	const params = { orgId };
	// Every tab carries the inherited `?farm=` scope forward (frontend
	// CLAUDE.md farm-scope rule) — and only that, so page-local params like
	// `?tab=` don't leak from one sub-section to the next.
	const tabs: TabItem[] = [
		{
			key: "overview",
			label: t("financial.nav.overview"),
			to: "/app/org/$orgId/financial",
			params,
			search: keepScope,
			icon: <LayoutDashboard className="h-4 w-4" />,
		},
		{
			key: "transactions",
			label: t("financial.nav.transactions"),
			to: "/app/org/$orgId/financial/transactions",
			params,
			search: keepScope,
			icon: <ArrowLeftRight className="h-4 w-4" />,
		},
		{
			key: "vendors",
			label: t("financial.nav.vendors"),
			to: "/app/org/$orgId/financial/vendors",
			params,
			search: keepScope,
			icon: <Truck className="h-4 w-4" />,
		},
		{
			key: "reports",
			label: t("financial.nav.reports"),
			to: "/app/org/$orgId/financial/reports",
			params,
			search: keepScope,
			icon: <BarChart3 className="h-4 w-4" />,
		},
		{
			key: "loans",
			label: t("financial.nav.loans"),
			to: "/app/org/$orgId/financial/loans",
			params,
			search: keepScope,
			icon: <Landmark className="h-4 w-4" />,
		},
		{
			key: "recurring",
			label: t("financial.nav.recurring"),
			to: "/app/org/$orgId/financial/recurring",
			params,
			search: keepScope,
			icon: <Repeat className="h-4 w-4" />,
		},
		{
			key: "receivables",
			label: t("financial.nav.receivables"),
			to: "/app/org/$orgId/financial/receivables",
			params,
			search: keepScope,
			icon: <HandCoins className="h-4 w-4" />,
		},
		{
			key: "payables",
			label: t("financial.nav.payables"),
			to: "/app/org/$orgId/financial/payables",
			params,
			search: keepScope,
			icon: <Receipt className="h-4 w-4" />,
		},
		// Salary data is sensitive: the tab only exists for payroll managers,
		// mirroring the PayrollGate on the routes themselves.
		...(can(Action.MANAGE_PAYROLL)
			? [
					{
						key: "payroll",
						label: t("financial.nav.payroll"),
						to: "/app/org/$orgId/financial/payroll",
						params,
						search: keepScope,
						icon: <Wallet className="h-4 w-4" />,
					},
				]
			: []),
	];

	const basePath = `/app/org/${orgId}/financial`;
	const activeTab =
		SUB_SECTIONS.find((section) =>
			location.pathname.startsWith(`${basePath}/${section}`),
		) ?? "overview";

	return (
		<div className="flex flex-col gap-4">
			{/* Section identity: the Financial Hub's own header + menu live INSIDE
			    the section (redesign handoff) — the global app bar / farm switcher
			    stay in the shell. */}
			<div className="flex items-center gap-2.5">
				{/* The icon badge is decorative and costs 42px of a 360px row; on
				    phones the header carries the sub-page CTAs instead, and the full
				    title ("Gestão financeira") only fits without it. */}
				<span className="hidden h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary sm:grid">
					<Wallet className="h-4 w-4" />
				</span>
				<span className="min-w-0 truncate font-display text-base font-bold sm:text-lg">
					{t("financial.nav.hubTitle")}
				</span>
				{/* Sub-pages portal their CTAs here (see FinancialHeaderActions) so
				    the header row carries them instead of a per-page action row. */}
				<HeaderSlotTarget
					name={FINANCIAL_HEADER_ACTIONS_SLOT}
					className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2"
				/>
			</div>
			<TabNavigation
				tabs={tabs}
				activeTab={activeTab}
				variant="underline"
				ariaLabel={t("financial.nav.title")}
			/>
			<Outlet />
		</div>
	);
}
